import React, { useState, useEffect, useRef } from 'react';
import ChatDisplay from './components/ChatDisplay';
import ChatInput from './components/ChatInput';
import VoiceControls from './components/VoiceControls';
import UserInput from './components/UserInput';
import ConfirmDialog from './components/ConfirmDialog';
import DebugInfo from './components/DebugInfo';
import PhaseTracker from './components/PhaseTracker';
import axios from 'axios';
import { 
  ChatMessage, InterviewPhase, InterviewStatus, PHASE_DISPLAY_NAMES, PHASE_TIMING, 
  InterviewProgress, StartInterviewRequest, GetInterviewStateResponse, ChatHistoryEntry, VoiceSettingsType 
} from './types';
import { buildApiUrl, buildWsUrl } from './config';

const App: React.FC = () => {
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [interviewId, setInterviewId] = useState<string | null>(null);
  const [interviewStatus, setInterviewStatus] = useState<InterviewStatus>(InterviewStatus.ACTIVE);
  const [currentPhase, setCurrentPhase] = useState<InterviewPhase>(InterviewPhase.INTRODUCTION_AND_BACKGROUND);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [pendingGreeting, setPendingGreeting] = useState<string | null>(null);
  const [progress, setProgress] = useState<InterviewProgress | null>(null);
  const [liveTranscription, setLiveTranscription] = useState<string>('');
  const [introductionReady, setIntroductionReady] = useState(false);
  const [preparingIntroduction, setPreparingIntroduction] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isTypingIntroduction, setIsTypingIntroduction] = useState(false);

  // User and session management
  const [userName, setUserName] = useState<string | null>(null);
  const [showUserInput, setShowUserInput] = useState(true);
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const [showNewSessionConfirm, setShowNewSessionConfirm] = useState(false);

  // Voice connection state
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isVoiceConnected, setIsVoiceConnected] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);

  // WebSocket connection for voice
  const [voiceWebSocket, setVoiceWebSocket] = useState<WebSocket | null>(null);

  // Voice settings
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettingsType>({
    speed: 1.0,
    volume: 1.0,
    pitch: 1.0
  });

  // VoiceControls ref for centralized message handling
  const voiceControlsRef = useRef<any>(null);

  // Check for existing session on mount
  useEffect(() => {
    console.log('App: Checking for existing session...');
    const savedInterviewId = localStorage.getItem('interviewId');
    const savedUserName = localStorage.getItem('userName');
    
    console.log('App: Saved session data:', { savedInterviewId, savedUserName });
    
    if (savedInterviewId && savedUserName && savedInterviewId.trim() !== '') {
      console.log('App: Valid session found, attempting restoration...');
      setIsRestoringSession(true);
      setUserName(savedUserName);
      restoreInterviewSession(savedInterviewId);
    } else {
      console.log('App: No valid session found, clearing localStorage and showing user input');
      // Clear invalid session data
      localStorage.removeItem('interviewId');
      localStorage.removeItem('userName');
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+N or Cmd+N for new session
      if ((event.ctrlKey || event.metaKey) && event.key === 'n') {
        event.preventDefault();
        if (!showUserInput) {
          setShowNewSessionConfirm(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showUserInput]);

  // Timer effect
  useEffect(() => {
    if (!startTime) return;
    
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime.getTime()) / 1000));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [startTime]);

  // Cleanup effect for typing interval
  useEffect(() => {
    return () => {
      if ((window as any).typingInterval) {
        clearInterval((window as any).typingInterval);
      }
    };
  }, []);

  // Fetch progress data
  const fetchProgress = async () => {
    if (!interviewId) {
      console.log('fetchProgress: No interviewId available, skipping progress fetch');
      return;
    }
    
    try {
      console.log(`fetchProgress: Fetching progress for interview ${interviewId}`);
      const response = await axios.get(buildApiUrl(`/api/interview/${interviewId}/progress`));
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  // Fetch progress when interview starts or when chat history changes
  useEffect(() => {
    console.log(`useEffect fetchProgress: interviewStarted=${interviewStarted}, interviewId=${interviewId}`);
    if (interviewStarted && interviewId) {
      fetchProgress();
    }
  }, [interviewStarted, interviewId, chatHistory]);

  // Function to restore interview session
  const restoreInterviewSession = async (savedInterviewId: string) => {
    console.log('restoreInterviewSession: Starting restoration with ID:', savedInterviewId);
    
    // Validate interview ID before making API call
    if (!savedInterviewId || savedInterviewId.trim() === '') {
      console.error('restoreInterviewSession: Invalid interview ID for session restoration');
      localStorage.removeItem('interviewId');
      localStorage.removeItem('userName');
      setShowUserInput(true);
      setIsRestoringSession(false);
      return;
    }

    try {
      setLoading(true);
      const apiUrl = buildApiUrl(`/api/interview/${savedInterviewId}/state`);
      console.log('restoreInterviewSession: Making API call to:', apiUrl);
      
      const response = await axios.get(apiUrl);
      const interviewState: GetInterviewStateResponse = response.data;
      
      console.log('restoreInterviewSession: API response received:', interviewState);
      
      // Check if interview is still active
      if (interviewState.interview_status === InterviewStatus.ACTIVE) {
        setInterviewId(interviewState.interview_id);
        setInterviewStatus(interviewState.interview_status);
        setCurrentPhase(interviewState.current_phase);
        setUserName(interviewState.user_name || 'Anonymous User');
        
        // Convert chat history to ChatMessage format
        const restoredChatHistory: ChatMessage[] = interviewState.chat_history.map(entry => ({
          role: entry.role as 'user' | 'model',
          parts: [{ text: entry.content }]
        }));
        
        setChatHistory(restoredChatHistory);
        
        // Set start time for elapsed time calculation
        if (interviewState.start_time) {
          const startDate = new Date(interviewState.start_time);
          setStartTime(startDate);
          setElapsedTime(Math.floor((Date.now() - startDate.getTime()) / 1000));
        }
        
        setInterviewStarted(true);
        setShowUserInput(false);
        console.log('✅ Interview session restored successfully');
      } else {
        // Interview is completed or aborted, clear saved session
        localStorage.removeItem('interviewId');
        localStorage.removeItem('userName');
        setShowUserInput(true);
        console.log('ℹ️ Previous interview was completed, starting fresh');
      }
    } catch (error: any) {
      console.error('restoreInterviewSession: Error restoring interview session:', error);
      console.error('restoreInterviewSession: Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url
      });
      // Clear invalid session data
      localStorage.removeItem('interviewId');
      localStorage.removeItem('userName');
      setShowUserInput(true);
    } finally {
      setLoading(false);
      setIsRestoringSession(false);
    }
  };

  // Function to start new interview
  const startNewInterview = async (userName: string) => {
    setLoading(true);
    try {
      const request: StartInterviewRequest = {
        user_name: userName
      };
      
      const res = await axios.post(buildApiUrl('/api/interview/start'), request);
      setInterviewId(res.data.interview_id);
      setInterviewStatus(res.data.interview_status);
      setCurrentPhase(res.data.current_phase);
      setUserName(res.data.user_name || userName);
      setStartTime(new Date());
      
      // Store session data
      localStorage.setItem('interviewId', res.data.interview_id);
      localStorage.setItem('userName', res.data.user_name || userName);
      
      // Store the greeting for later playback after user interaction
      setPendingGreeting(res.data.bot_message);
      
      setShowUserInput(false);
      
    } catch (err) {
      console.error('Interview start error:', err);
      setChatHistory([
        { role: 'model', parts: [{ text: 'Failed to start interview. Please try again.' }] }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Function to start a completely new session
  const startNewSession = () => {
    // Clear all current state
    setChatHistory([]);
    setInput('');
    setInterviewId(null);
    setInterviewStatus(InterviewStatus.ACTIVE);
    setCurrentPhase(InterviewPhase.INTRODUCTION_AND_BACKGROUND);
    setStartTime(null);
    setElapsedTime(0);
    setInterviewStarted(false);
    setPendingGreeting(null);
    setProgress(null);
    setLiveTranscription('');
    setIntroductionReady(false);
    setPreparingIntroduction(false);
    setCurrentWordIndex(0);
    setIsTypingIntroduction(false);
    setUserName(null);
    
    // Clear session storage
    localStorage.removeItem('interviewId');
    localStorage.removeItem('userName');
    
    // Show user input form
    setShowUserInput(true);
  };

  // Function to start the interview with voice
  const startInterviewWithVoice = async () => {
    if (pendingGreeting) {
      setInterviewStarted(true);
      setPreparingIntroduction(true);
      
      try {
        // Prepare the voice synthesis first
        const { audio, duration } = await synthesizeAndPlayGreeting(pendingGreeting);
        
        // Play the introduction audio first
        await playIntroductionAudio();
        
        // Start the word-by-word typing effect with a small delay
        setTimeout(() => {
          startTypingEffect(pendingGreeting, duration);
        }, 500); // 500ms delay to let audio start
        
      } catch (error) {
        console.error('Error preparing introduction:', error);
        // Fallback: show message even if voice fails
        setChatHistory([
          { role: 'model', parts: [{ text: pendingGreeting }] }
        ]);
        setIntroductionReady(true);
      } finally {
        setPreparingIntroduction(false);
        setPendingGreeting(null);
      }
    }
  };

  // Function to synthesize and play the initial greeting immediately
  const synthesizeAndPlayGreeting = async (text: string): Promise<{ audio: HTMLAudioElement, duration: number }> => {
    try {
      console.log('🎤 Synthesizing initial greeting...');
      
      // Call the TTS API with correct JSON format
      const response = await axios.post(buildApiUrl('/api/voice/tts'), {
        text: text,
        voice_settings: voiceSettings
      });
      
      if (response.data.success) {
        // Convert base64 audio to blob
        const audioData = atob(response.data.audio);
        const audioArray = new Uint8Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          audioArray[i] = audioData.charCodeAt(i);
        }
        
        const audioBlob = new Blob([audioArray], { type: `audio/${response.data.format}` });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Create audio element and prepare it
        const audio = new Audio(audioUrl);
        
        // Return a promise that resolves when the audio is loaded and ready
        return new Promise((resolve, reject) => {
          audio.oncanplaythrough = () => {
            console.log('✅ Voice greeting ready to play');
            
            // Set up playback event handlers
            audio.onplay = () => {
              console.log('🔊 Playing initial greeting...');
              setIsPlayingAudio(true);
            };
            
            audio.onended = () => {
              console.log('✅ Initial greeting finished playing');
              setIsPlayingAudio(false);
              URL.revokeObjectURL(audioUrl);
            };
            
            audio.onerror = (error) => {
              console.error('❌ Error playing initial greeting:', error);
              setIsPlayingAudio(false);
              URL.revokeObjectURL(audioUrl);
            };
            
            // Store the audio element for later playback
            (window as any).introductionAudio = audio;
            
            resolve({ audio, duration: audio.duration });
          };
          
          audio.onerror = (error) => {
            console.error('❌ Error loading voice greeting:', error);
            reject(error);
          };
          
          // Start loading the audio
          audio.load();
        });
        
      } else {
        console.error('❌ TTS API returned error:', response.data);
        throw new Error('TTS API error');
      }
      
    } catch (error) {
      console.error('❌ Error synthesizing initial greeting:', error);
      throw error;
    }
  };

  // Function to play the prepared introduction audio
  const playIntroductionAudio = async () => {
    try {
      const audio = (window as any).introductionAudio;
      if (audio) {
        await audio.play();
        console.log('✅ Introduction audio started playing');
      }
    } catch (error) {
      console.log('ℹ️ Autoplay blocked by browser - continuing with text-only mode');
      setIsPlayingAudio(false);
    }
  };

  // Function to start the word-by-word typing effect
  const startTypingEffect = (text: string, audioDuration: number) => {
    const words = text.split(' ');
    setCurrentWordIndex(0);
    setIsTypingIntroduction(true);
    
    // Calculate timing based on actual audio duration
    const millisecondsPerWord = (audioDuration * 1000) / words.length;
    
    console.log(`🎯 Starting typing effect: ${words.length} words over ${audioDuration}s (${millisecondsPerWord.toFixed(1)}ms per word)`);
    
    // Add the message to chat history with empty text initially
    setChatHistory([
      { role: 'model', parts: [{ text: '' }] }
    ]);
    
    // Start the typing effect
    const typingInterval = setInterval(() => {
      setCurrentWordIndex(prevIndex => {
        const newIndex = prevIndex + 1;
        
        if (newIndex <= words.length) {
          // Update the message with current words
          const currentText = words.slice(0, newIndex).join(' ');
          setChatHistory(prev => [
            { ...prev[0], parts: [{ text: currentText }] }
          ]);
          
          return newIndex;
        } else {
          // Typing effect complete
          clearInterval(typingInterval);
          setIsTypingIntroduction(false);
          setIntroductionReady(true);
          return prevIndex;
        }
      });
    }, millisecondsPerWord);
    
    // Store the interval ID to clear it if needed
    (window as any).typingInterval = typingInterval;
  };

  // Function to synthesize and play agent response with voice
  const synthesizeAndPlayResponse = async (text: string): Promise<void> => {
    try {
      console.log('🎤 Synthesizing agent response for text:', text);
      console.log('🎤 Voice enabled:', voiceEnabled);
      
      // Call the TTS API with correct JSON format - use backend defaults
      const response = await axios.post(buildApiUrl('/api/voice/tts'), {
        text: text,
        voice_settings: voiceSettings
      });
      
      console.log('🎤 TTS API response received:', response.data.success);
      
      if (response.data.success) {
        console.log('🎤 Converting audio data...');
        // Convert base64 audio to blob
        const audioData = atob(response.data.audio);
        const audioArray = new Uint8Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          audioArray[i] = audioData.charCodeAt(i);
        }
        
        const audioBlob = new Blob([audioArray], { type: `audio/${response.data.format}` });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        console.log('🎤 Audio blob created, size:', audioBlob.size);
        
        // Create audio element and prepare it
        const audio = new Audio(audioUrl);
        
        // Start real-time transcribing effect
        const words = text.split(' ');
        let currentWordIndex = 0;
        
        // Add the message to chat history with empty text initially
        setChatHistory(prev => [...prev, { role: 'model', parts: [{ text: '' }] }]);
        
        // Set up playback event handlers
        audio.onplay = () => {
          console.log('🔊 Playing agent response...');
          setIsPlayingAudio(true);
          
          // Start the typing effect when audio starts
          const audioDuration = audio.duration || 3; // fallback duration
          const millisecondsPerWord = (audioDuration * 1000) / words.length;
          
          console.log(`🎯 Starting typing effect: ${words.length} words over ${audioDuration}s (${millisecondsPerWord.toFixed(1)}ms per word)`);
          
          const typingInterval = setInterval(() => {
            currentWordIndex++;
            
            if (currentWordIndex <= words.length) {
              // Update the message with current words
              const currentText = words.slice(0, currentWordIndex).join(' ');
              setChatHistory(prev => {
                const newHistory = [...prev];
                const lastMessage = newHistory[newHistory.length - 1];
                if (lastMessage && lastMessage.role === 'model') {
                  lastMessage.parts[0].text = currentText;
                }
                return newHistory;
              });
            } else {
              // Typing effect complete
              clearInterval(typingInterval);
            }
          }, millisecondsPerWord);
        };
        
        audio.onended = () => {
          console.log('✅ Agent response finished playing');
          setIsPlayingAudio(false);
          URL.revokeObjectURL(audioUrl);
        };
        
        audio.onerror = (error) => {
          console.error('❌ Error playing agent response:', error);
          setIsPlayingAudio(false);
          URL.revokeObjectURL(audioUrl);
        };
        
        // Start playing the audio
        console.log('🎤 Attempting to play audio...');
        try {
          await audio.play();
          console.log('🎤 Audio play() called successfully');
        } catch (playError) {
          console.error('❌ Audio play() failed:', playError);
          console.log('ℹ️ This might be due to browser autoplay policy. Audio is ready but needs user interaction.');
          // Try to play on next user interaction
          const playOnInteraction = () => {
            audio.play().then(() => {
              console.log('🎤 Audio started playing after user interaction');
            }).catch((err) => {
              console.error('❌ Audio still failed after user interaction:', err);
            });
            document.removeEventListener('click', playOnInteraction);
            document.removeEventListener('keydown', playOnInteraction);
          };
          document.addEventListener('click', playOnInteraction);
          document.addEventListener('keydown', playOnInteraction);
        }
        
      } else {
        console.error('❌ TTS API returned error:', response.data);
        // Continue with text-only if voice fails
      }
      
    } catch (error) {
      console.error('❌ Error synthesizing agent response:', error);
      console.error('❌ Error details:', (error as any).response?.data || (error as Error).message);
      // Continue with text-only if voice fails
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !interviewId) return;
    console.log('📤 handleSend called with input:', input);
    setLoading(true);
    const userMsg: ChatMessage = { role: 'user', parts: [{ text: input }] };
    setChatHistory(prev => [...prev, userMsg]);
    setInput('');
    try {
      // Use full URL for consistency with start interview
      const res = await axios.post(buildApiUrl(`/api/interview/${interviewId}/respond`), {
        user_input: input,
        chat_history: [...chatHistory, userMsg],
      });
      
      const botMessage = res.data.bot_message;
      console.log('🤖 Bot response received:', botMessage);
      
      // Don't add the bot message here - synthesizeAndPlayResponse will handle it with typing effect
      setInterviewStatus(res.data.interview_status);
      setCurrentPhase(res.data.current_phase);
      
      // Synthesize and play voice for the agent response
      console.log('🎤 Voice enabled check:', voiceEnabled);
      if (voiceEnabled) {
        console.log('🎤 Calling synthesizeAndPlayResponse...');
        await synthesizeAndPlayResponse(botMessage);
      } else {
        console.log('🎤 Voice is disabled, adding text-only message');
        // Add text-only message if voice is disabled
        setChatHistory(prev => [...prev, { role: 'model', parts: [{ text: botMessage }] }]);
      }
      
      // Fetch updated progress after response
      await fetchProgress();
    } catch (err) {
      console.error('Response error:', err);
      setChatHistory(prev => [...prev, { role: 'model', parts: [{ text: 'Error processing your response.' }] }]);
    } finally {
      setLoading(false);
    }
  };

  // Handle voice messages
  const handleVoiceMessage = (text: string) => {
    
    // Always append new user voice message (no search/replace)
    const userMsg: ChatMessage = { role: 'user', parts: [{ text }] };
    setChatHistory(prev => {
      const newHistory = [...prev, userMsg];
      return newHistory;
    });
    
    // Clear live transcription
    setLiveTranscription('');
  };

  // Handle voice responses
  const handleVoiceResponse = (audioData: string, text: string) => {
    console.log('🎤 handleVoiceResponse called with text:', text);
    console.log('🎤 Current chat history length before adding voice response:', chatHistory.length);
    
    // This function is called when VoiceControls starts playing audio
    // VoiceControls handles the audio playback, we just need to add the text to chat history
    
    // Add the AI response to chat history (same as text messages)
    const aiMsg: ChatMessage = { role: 'model', parts: [{ text }] };
    setChatHistory(prev => {
      const newHistory = [...prev, aiMsg];
      console.log('🎤 Added voice response to chat history, new length:', newHistory.length);
      return newHistory;
    });

    // FIX: Update current phase from backend response for voice messages
    // Voice responses should update the phase just like text responses
    // The backend already processes voice messages and returns the correct current_phase
    // We need to fetch the latest interview state to get the updated current_phase
    const updatePhaseFromBackend = async () => {
      if (interviewId) {
        try {
          const response = await axios.get(buildApiUrl(`/api/interview/${interviewId}/state`));
          const interviewState = response.data;
          if (interviewState.current_phase) {
            console.log('🎤 Updating current phase from voice response:', interviewState.current_phase);
            setCurrentPhase(interviewState.current_phase);
          }
        } catch (error) {
          console.error('🎤 Error updating phase from voice response:', error);
        }
      }
    };
    
    // Update phase from backend
    updatePhaseFromBackend();

    // Fetch updated progress after voice response
    fetchProgress();
  };

  // Handle empty audio errors
  const handleEmptyAudioError = (message: string) => {
    console.log('🎤 handleEmptyAudioError called with message:', message);
    
    // Add the error message to chat history as an AI response
    const aiMsg: ChatMessage = { role: 'model', parts: [{ text: message }] };
    setChatHistory(prev => {
      const newHistory = [...prev, aiMsg];
      console.log('🎤 Added empty audio error to chat history, new length:', newHistory.length);
      return newHistory;
    });
    
    // Reset voice processing state
    setIsVoiceProcessing(false);
  };

  // Initialize WebSocket connection for voice
  useEffect(() => {
    if (!interviewId || !voiceEnabled) return;

    const wsUrl = buildWsUrl(`/api/voice/ws/${interviewId}`);
    console.log('🔌 Attempting WebSocket connection to:', wsUrl);
    
    const websocket = new WebSocket(wsUrl);
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 3;
    const reconnectDelay = 2000; // 2 seconds
    let pingInterval: NodeJS.Timeout | null = null;
    let pongTimeout: NodeJS.Timeout | null = null;

    const connectWebSocket = () => {
      console.log(`🔌 WebSocket connection attempt ${reconnectAttempts + 1}/${maxReconnectAttempts + 1}`);
      
      websocket.onopen = () => {
        setIsVoiceConnected(true);
        reconnectAttempts = 0; // Reset on successful connection
        console.log('✅ Voice WebSocket connected successfully');
        
        // Start ping/pong mechanism
        pingInterval = setInterval(() => {
          if (websocket.readyState === WebSocket.OPEN) {
            try {
              websocket.send(JSON.stringify({ type: 'ping' }));
              console.log('🏓 Sent ping');
              
              // Set pong timeout - increased for long audio processing
              pongTimeout = setTimeout(() => {
                console.error('❌ Pong timeout - connection may be dead');
                websocket.close(1000, 'Pong timeout');
              }, 30000); // 30 second timeout (increased from 5s)
            } catch (error) {
              console.error('❌ Error sending ping:', error);
            }
          }
        }, 60000); // Send ping every 60 seconds (increased from 30s)
      };

      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle pong response
          if (data.type === 'pong') {
            console.log('🏓 Received pong');
            if (pongTimeout) {
              clearTimeout(pongTimeout);
              pongTimeout = null;
            }
            return;
          }
          
          // Forward voice-specific messages to VoiceControls
          if (data.type === 'response_audio_chunk' || 
              data.type === 'response_text' || 
              data.type === 'response_audio' ||
              data.type === 'transcription' ||
              data.type === 'transcription_partial' ||
              data.type === 'transcription_error' ||
              data.type === 'empty_audio_error' ||
              data.type === 'error') {
            console.log('App forwarding voice-specific message to VoiceControls:', data.type);
            if (voiceControlsRef.current) {
              voiceControlsRef.current.handleMessage(data);
            }
            return;
          }
          
          // Handle other non-voice messages
          if (data.type === 'chunk_transcription') {
            console.log('App received non-voice WebSocket message: chunk_transcription');
            // Handle chunk transcription if needed
          } else if (data.type === 'chunk_error') {
            console.log('App received non-voice WebSocket message: chunk_error');
            // Handle chunk errors if needed
          } else if (data.type !== 'audio_received') {
            console.log('App received non-voice WebSocket message:', data.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      websocket.onclose = (event) => {
        setIsVoiceConnected(false);
        console.log('❌ Voice WebSocket disconnected:', event.code, event.reason);
        
        // Clean up ping/pong timers
        if (pingInterval) {
          clearInterval(pingInterval);
          pingInterval = null;
        }
        if (pongTimeout) {
          clearTimeout(pongTimeout);
          pongTimeout = null;
        }
        
        // Attempt reconnection if not a normal closure and we haven't exceeded max attempts
        if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
          console.log(`🔄 Attempting to reconnect in ${reconnectDelay}ms...`);
          setTimeout(() => {
            reconnectAttempts++;
            connectWebSocket();
          }, reconnectDelay);
        } else if (reconnectAttempts >= maxReconnectAttempts) {
          console.error('❌ Max reconnection attempts reached. WebSocket connection failed.');
        }
      };

      websocket.onerror = (error) => {
        console.error('❌ Voice WebSocket error:', error);
        setIsVoiceConnected(false);
      };
    };

    connectWebSocket();
    setVoiceWebSocket(websocket);

    return () => {
      console.log('🧹 Cleaning up WebSocket connection');
      
      // Clean up timers
      if (pingInterval) {
        clearInterval(pingInterval);
      }
      if (pongTimeout) {
        clearTimeout(pongTimeout);
      }
      
      websocket.close(1000, 'Component unmounting');
      setVoiceWebSocket(null);
    };
  }, [interviewId, voiceEnabled]);



  // Handle regular audio data from VoiceControls (non-chunked) - REMOVED
  // VoiceControls no longer handles audio sending to prevent duplicates
  // All audio sending is now handled by ChatInput/VoiceRecorder
  const handleVoiceControlsAudioData = async (audioBlob: Blob) => {
    console.log('🎤 VoiceControls audio handler removed to prevent duplicates');
    console.log('🎤 Audio should be handled by ChatInput/VoiceRecorder only');
    // Do nothing - let ChatInput handle all audio sending
  };

  // Handle audio data from ChatInput
  const handleChatInputAudioData = async (audioBlob: Blob) => {
    if (!voiceWebSocket || voiceWebSocket.readyState !== WebSocket.OPEN) {
      console.error('❌ WebSocket not connected');
      return;
    }
    
    try {
      // Set voice processing to true immediately when audio is sent
      setIsVoiceProcessing(true);
      console.log('🎤 ChatInput: Voice processing started');
      
      const arrayBuffer = await audioBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const base64Audio = btoa(Array.from(uint8Array).map(byte => String.fromCharCode(byte)).join(''));
      
      // Prepare message for audio
      const message = {
        type: 'audio',
        audio: base64Audio
      };
      
      console.log('📤 ChatInput: Sending audio to WebSocket:', {
        size: audioBlob.size,
        timestamp: new Date().toISOString()
      });
      
      voiceWebSocket.send(JSON.stringify(message));
      console.log('✅ ChatInput: Audio data sent to WebSocket successfully');
    } catch (error) {
      console.error('❌ ChatInput: Failed to send audio data:', error);
      // Reset voice processing on error
      setIsVoiceProcessing(false);
    }
  };



  // Handle live transcription updates
  const handleLiveTranscriptionUpdate = (text: string) => {
    setLiveTranscription(text);
    
    if (text.trim()) {
      console.log('🎤 handleLiveTranscriptionUpdate called with text:', text);
      
      // Always append new live transcription message (no search/replace)
      const liveMsg: ChatMessage = { 
        role: 'live_transcription', 
        parts: [{ text }],
        isLive: true 
      };
      
      setChatHistory(prev => {
        // Only append if the last message is not already a live transcription with same text
        const lastMessage = prev[prev.length - 1];
        if (lastMessage?.role === 'live_transcription' && lastMessage.parts[0]?.text === text) {
          console.log('🎤 Skipping duplicate live transcription message');
          return prev; // Don't add duplicate
        }
        
        const newHistory = [...prev, liveMsg];
        console.log('🎤 Added live transcription message, new length:', newHistory.length);
        return newHistory;
      });
    }
    // No removal logic needed - let messages accumulate naturally
  };

  // Handle audio playback events
  const handlePlaybackStart = () => {
    setIsPlayingAudio(true);
  };

  const handlePlaybackEnd = () => {
    setIsPlayingAudio(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseNumber = (phase: InterviewPhase): number => {
    const phases = [
      InterviewPhase.INTRODUCTION_AND_BACKGROUND,
      InterviewPhase.METHODOLOGY_OVERVIEW,
      InterviewPhase.SCENARIO_AND_TACIT_KNOWLEDGE
    ];
    return phases.indexOf(phase) + 1;
  };

  const getQuestionProgress = () => {
    if (!progress) return 0;
    return progress.progress_percentage;
  };

  // Voice toggle handler
  const handleVoiceToggle = () => {
    setVoiceEnabled(!voiceEnabled);
  };

  // Voice settings toggle handler
  const handleVoiceSettingsToggle = () => {
    setShowVoiceSettings(!showVoiceSettings);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <DebugInfo />
      
      {/* Loading screen while restoring session */}
      {isRestoringSession && (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Restoring your previous session...</p>
          </div>
        </div>
      )}

      {/* Show user input form if needed */}
      {showUserInput && (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
          {isRestoringSession ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Restoring your previous session...</p>
            </div>
          ) : (
            <UserInput 
              onSubmit={startNewInterview}
              isLoading={loading}
              onNewSession={startNewSession}
              showNewSessionButton={true}
            />
          )}
        </div>
      )}

      {/* Show main interview interface */}
      {!showUserInput && (
        <div className="h-screen bg-gray-50 flex flex-col items-center justify-center p-4 overflow-hidden">
          <div className="w-full max-w-7xl bg-white rounded-lg shadow-lg p-6 flex flex-col h-full max-h-full">
            <div className="mb-4 flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">AI Interviewer</h1>
                <p className="text-sm text-gray-600 mt-1">
                  
                  {userName && (
                    <span className=" text-gray-600">Interviewing: {userName}</span>
                  )}
                </p>
              </div>
              
              {/* New Session Button */}
              <button
                onClick={() => setShowNewSessionConfirm(true)}
                className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 group relative"
                title="Start a completely new interview session (Ctrl+N)"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>New Session</span>
                
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  Ctrl+N
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </button>
            </div>

            {/* Phase and Progress Display */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 hidden">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-800">Current Phase:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    Phase {getPhaseNumber(currentPhase)} (of 5): {PHASE_DISPLAY_NAMES[currentPhase]}
                  </span>
                </div>
                <div className="text-sm text-gray-700">
                  {progress ? `${progress.completed_questions}/${progress.total_questions} questions completed` : 'Loading...'}
                </div>
              </div>
              
              {/* Question Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gray-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getQuestionProgress()}%` }}
                ></div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex overflow-hidden">
              {/* Phase Tracker Sidebar */}
              {interviewStarted && !preparingIntroduction && (
                <div className="w-80 flex-shrink-0 mr-6 overflow-y-auto">
                  <PhaseTracker 
                    interviewId={interviewId}
                    currentPhase={currentPhase}
                  />
                </div>
              )}
              
              {/* Chat Area */}
              <div className="flex-1 flex flex-col">
                {!interviewStarted ? (
                  // Show start button before interview begins
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                  
                      <h2 className="text-2xl font-bold text-gray-800 mb-4">Ready to Start Your Interview?</h2>
                      <p className="text-gray-600 mb-8 max-w-md">
                        Your AI interviewer is ready to begin. Click the button below to start your voice interview.
                      </p>
                      <button
                        onClick={startInterviewWithVoice}
                        disabled={loading}
                        className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-semibold py-4 px-8 rounded-lg transition-colors text-lg"
                      >
                        {loading ? 'Preparing...' : 'Start the Interview'}
                      </button>
                    </div>
                  </div>
                ) : preparingIntroduction ? (
                  // Show loading indicator while preparing introduction
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-6xl mb-6 animate-pulse">🎤</div>
                      <h2 className="text-2xl font-bold text-gray-800 mb-4">Preparing Your Interview</h2>
                      <p className="text-gray-600 mb-8 max-w-md">
                        Your AI interviewer is preparing to speak. This will just take a moment...
                      </p>
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Show chat interface after introduction is ready
                  <>
                    <div className="flex-1 max-h-[calc(100vh-280px)]">
                      <ChatDisplay 
                        chatHistory={chatHistory} 
                        isTypingIntroduction={isTypingIntroduction}
                        isLoading={loading}
                        isVoiceProcessing={isVoiceProcessing}
                      />
                    </div>
                    
                    {/* Input Area with Integrated Voice Controls */}
                    <div className="mt-4 flex-shrink-0">
                      <ChatInput
                        value={input}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setInput(e.target.value)}
                        onSend={handleSend}
                        disabled={loading || interviewStatus !== InterviewStatus.ACTIVE || isPlayingAudio || isTypingIntroduction}
                        loading={loading}
                        interviewId={interviewId}
                        onVoiceMessage={handleVoiceMessage}
                        onVoiceResponse={handleVoiceResponse}
                        isPlaying={isPlayingAudio}
                        onPlaybackStart={handlePlaybackStart}
                        onPlaybackEnd={handlePlaybackEnd}
                        onLiveTranscriptionUpdate={handleLiveTranscriptionUpdate}
                        voiceEnabled={voiceEnabled}
                        isVoiceConnected={isVoiceConnected}
                        isVoiceProcessing={isVoiceProcessing}
                        onVoiceToggle={handleVoiceToggle}
                        onVoiceSettingsToggle={handleVoiceSettingsToggle}
                        showVoiceSettings={showVoiceSettings}
                        onWebSocketAudioData={handleChatInputAudioData}
                        showTextInput={false}
                      />
                      
                      {/* Voice Controls */}
                      <VoiceControls
                        ref={voiceControlsRef}
                        interviewId={interviewId}
                        onVoiceMessage={handleVoiceMessage}
                        onVoiceResponse={handleVoiceResponse}
                        onEmptyAudioError={handleEmptyAudioError}
                        disabled={loading || interviewStatus !== InterviewStatus.ACTIVE || isPlayingAudio || isTypingIntroduction}
                        isPlaying={isPlayingAudio}
                        onPlaybackStart={handlePlaybackStart}
                        onPlaybackEnd={handlePlaybackEnd}
                        onConnectionStatusChange={setIsVoiceConnected}
                        onProcessingStatusChange={setIsVoiceProcessing}
                        webSocket={voiceWebSocket}
                        voiceSettings={voiceSettings}
                        setVoiceSettings={setVoiceSettings}
                        onLiveTranscriptionUpdate={handleLiveTranscriptionUpdate}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Interview Status */}
            {interviewStatus === InterviewStatus.COMPLETED && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-green-800 font-semibold">Interview completed!</div>
                <div className="text-green-700 text-sm mt-1">
                  Thank you for participating in our research on user interview methods.
                </div>
                <button
                  onClick={() => {
                    localStorage.removeItem('interviewId');
                    localStorage.removeItem('userName');
                    setShowUserInput(true);
                    setInterviewStarted(false);
                    setChatHistory([]);
                    setInterviewId(null);
                  }}
                  className="mt-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm"
                >
                  Start New Interview
                </button>
              </div>
            )}
          </div>
        </div>
      )}

             {/* Confirm Dialog for Starting a New Session */}
       <ConfirmDialog
         isOpen={showNewSessionConfirm}
         title="Start a New Session"
         message="Are you sure you want to start a new session? This will clear all current conversation history and progress."
         onConfirm={() => {
           startNewSession();
           setShowNewSessionConfirm(false);
         }}
         onCancel={() => setShowNewSessionConfirm(false)}
         confirmText="Start New Session"
       />
    </div>
  );
};

export default App; 